import { ImageCarousel } from 'shared/components/ImageCarousel/ImageCarousel';
import { useConfig } from 'shared/config/ConfigContext';
import { monthNames } from 'shared/util/calendar';
import { thousandSeparated } from 'shared/util/formatting';
import { useUnleash } from '@/hooks/unleash';
import type { Filters, Offer } from '@/interfaces';
import { getNumberOfPax, getPricePerPerson, getPriceTotal } from '@/lib/utils/hotelData';
import { addHotelPageFiltersToUrl, browserQueryParamFor } from '@/lib/utils/queryParamFilterConversions';
import { trackSeeMoreOffers } from '@/lib/utils/tracking';
import { SupplierLogo } from '../images/SupplierLogo';
import { StarRating } from './StarRating';

export interface ResultListItemProps {
    offer: Offer;
    filters: Filters;
}

const meals: { [index: string]: string } = {
    all_inclusive: 'All inclusive',
    breakfast: 'Frokost',
    half_pension: 'Halvpensjon',
    full_pension: 'Fullpensjon',
};

export const ResultListItemClickable = ({ offer, filters }: ResultListItemProps) => {
    const { contextPathPP, imgPath } = useConfig();
    const unleash = useUnleash();

    const numPax = getNumberOfPax(offer.numberOfAdults, offer.numberOfChildren);
    const pricePerPerson = thousandSeparated(getPricePerPerson(offer.pricePerPerson, numPax, offer.priceTotal));
    const priceTotal = thousandSeparated(getPriceTotal(offer.pricePerPerson, numPax, offer.priceTotal));

    const hotelUrl = addHotelPageFiltersToUrl(`${contextPathPP}/hotell?${browserQueryParamFor('hotelId', offer.hotelId)}`, filters);

    const handleOfferClick = () => {
        trackSeeMoreOffers(offer.offerId, offer.hotelName);
    };

    const joinLocation = (...parts: Array<string | null | undefined>) =>
        parts
            .map((s) => s?.trim())
            .filter((s): s is string => !!s && s.length > 0)
            .join(', ');

    const getFormattedTravelDates = (dateStringDeparture: string, dateStringReturn: string) => {
        const departureDate = new Date(dateStringDeparture);
        const returnDate = new Date(dateStringReturn);
        const departureMonth = monthNames[departureDate.getMonth()].slice(0, 3);
        const returnMonth = monthNames[returnDate.getMonth()].slice(0, 3);

        if (departureMonth === returnMonth) {
            return `${departureDate.getDate()}.–${returnDate.getDate()}. ${departureMonth}`;
        } else {
            return `${departureDate.getDate()}. ${departureMonth} – ${returnDate.getDate()}. ${returnMonth}`;
        }
    };

    const images = offer.imagePaths.map((path) => `${imgPath}480w${path}`);

    return (
        <div className="overflow-hidden relative rounded-8 s-surface-elevated-200">
            <article
                className="flex flex-col border s-border rounded-8 md:flex-row md:min-h-[186px]"
                data-testid={`resultListItem-${offer.offerId}`}
                data-result-item
                tabIndex={-1}>
                <div className="relative z-10 md:w-1/2 w-full md:max-w-[400px] md:h-[186px] md:aspect-auto overflow-hidden rounded-tl-8 rounded-tr-8 md:rounded-tr-0 md:rounded-bl-8">
                    {unleash?.imageCarousel ? (
                        <ImageCarousel images={images} alt={offer.hotelName ?? 'Hotellbilde'} className="h-full" />
                    ) : (
                        <img src={images[0]} alt={offer.hotelName ?? 'Hotellbilde'} className="w-full h-full object-cover" />
                    )}
                </div>

                <a
                    href={hotelUrl}
                    onClick={handleOfferClick}
                    className="flex flex-col md:flex-row w-full justify-between no-underline hover:no-underline focusable s-text"
                    rel="nofollow"
                    aria-label={`Se tilbud for ${offer.hotelName}`}
                    data-testid={`resultDetailLink-${offer.hotelId}`}>
                    <header className="flex flex-col gap-16 p-16">
                        <div className="flex flex-row justify-between items-center">
                            <StarRating id={`rating-${offer.offerId}`} rating={offer.rating} emptyStars={true} />
                            <SupplierLogo supplier={offer.supplier} className="md:hidden" />
                        </div>

                        <div>
                            <h2 className="t4 mb-4" data-testid="offerHotelName" id={`resultListItem-${offer.offerId}`}>
                                {offer.hotelName}
                            </h2>
                            <p className="s-text-subtle text-xs mb-0" data-testid="offerDestination">
                                {joinLocation(offer.destination, offer.region, offer.country)}
                            </p>
                        </div>
                        <p className="text-caption font-normal flex">
                            <w-icon name="Charter" size="small" locale="nb" className="pr-4" aria-hidden></w-icon>
                            {` ${getFormattedTravelDates(offer.departureDate, offer.returnDate)} • ${offer.duration} dager`}
                            {offer.board && offer.board !== 'no_meals' && (
                                <span className="pl-4" data-testid="offerMeal">
                                    {` • ${meals[offer.board]}`}
                                </span>
                            )}
                        </p>
                    </header>

                    <footer className="flex flex-col p-16 justify-between md:min-w-[144px] border-t md:border-t-[0] md:border-l s-border md:items-end">
                        <SupplierLogo supplier={offer.supplier} className="hidden md:block" />

                        <div className="flex flex-col gap-16">
                            <div className="flex flex-row md:flex-col items-baseline md:items-end gap-8 md:gap-4">
                                <p className="t3 m-0">{`${pricePerPerson} kr`}</p>
                                <p className="text-detail m-0">{`${priceTotal} kr/${numPax} personer`}</p>
                            </div>
                            <div
                                className="hidden md:flex justify-center w-full max-w-full py-12 px-16 border-0 rounded-8 font-bold text-m leading-[24] s-text-inverted bg-[--w-color-button-primary-background]"
                                aria-hidden="true">
                                Se tilbud
                            </div>
                        </div>
                    </footer>
                </a>
            </article>
        </div>
    );
};
